import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import usersService from '../../../../services/users.service';
import { useEyeconxStore } from '../../../../store/eyeconxStore';
import { showError, showSuccess } from '../../../../utils/ecxSwal';
import ClinicSettingsCancelButton from '../clinicsettings/subparts/ClinicSettingsCancelButton';
import ClinicSettingsFormFooter from '../clinicsettings/subparts/ClinicSettingsFormFooter';
import ClinicSettingsListSkeleton from '../clinicsettings/subparts/ClinicSettingsListSkeleton';
import {
    CHANNEL_KEYS,
    REMINDER_CATEGORIES,
    ReminderOverviewStats,
    ReminderSettingsPanels,
    TOGGLE_KEYS,
    getDefaultReminderSettings,
    getEcpDisplayName,
    normalizeSettings,
    stripToReminderPayload,
} from '../reminders/reminderSettingsShared';
import './UserReminder.css';

const UserReminder = ({ show, user, onClose }) => {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);
    const [settings, setSettings] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [settingsMissing, setSettingsMissing] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const {
        register,
        handleSubmit,
        control,
        reset,
        watch,
        formState: { errors },
    } = useForm();

    const formValues = watch();
    const userName = getEcpDisplayName(user);
    const fieldIdPrefix = user?.id || 'user-reminder';

    const fetchSettings = useCallback(async () => {
        if (!clinicId || !user?.id) {
            return;
        }

        setIsLoading(true);
        setLoadFailed(false);
        setSettingsMissing(false);

        try {
            const response = await usersService.getUserReminder(clinicId, user.id);
            const normalized = normalizeSettings(response.data);
            setSettings(normalized);
            reset(normalized);
        } catch (error) {
            console.error(error);

            if (error?.response?.status === 404) {
                const defaults = getDefaultReminderSettings();
                setSettings(defaults);
                reset(defaults);
                setSettingsMissing(true);
            } else {
                setLoadFailed(true);
            }
        } finally {
            setIsLoading(false);
        }
    }, [clinicId, reset, user?.id]);

    useEffect(() => {
        if (show && user) {
            setIsEditing(false);
            fetchSettings();
        }
    }, [show, user, fetchSettings]);

    useEffect(() => {
        if (!show) {
            return undefined;
        }

        const handleEscape = (event) => {
            if (event.key === 'Escape' && !isSaving) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [show, onClose, isSaving]);

    const allItems = useMemo(
        () => REMINDER_CATEGORIES.flatMap((category) => category.items),
        []
    );

    const currentValues = isEditing ? formValues : settings;

    const enabledCount = useMemo(() => {
        if (!currentValues) {
            return 0;
        }

        return TOGGLE_KEYS.filter((key) => currentValues[key]).length;
    }, [currentValues]);

    const activeChannels = useMemo(() => {
        if (!currentValues) {
            return 0;
        }

        return CHANNEL_KEYS.filter((key) => currentValues[key]).length;
    }, [currentValues]);

    const handleEdit = () => {
        reset(settings || getDefaultReminderSettings());
        setIsEditing(true);
    };

    const handleCancel = () => {
        reset(settings || getDefaultReminderSettings());
        setIsEditing(false);
    };

    const onSubmit = async (data) => {
        setIsSaving(true);

        try {
            const response = await usersService.updateUserReminder(
                clinicId,
                user.id,
                stripToReminderPayload(data)
            );
            const normalized = normalizeSettings(response.data);
            setSettings(normalized);
            reset(normalized);
            setSettingsMissing(false);
            setIsEditing(false);

            await showSuccess({
                title: 'Settings Saved',
                text: 'Your reminder settings were updated successfully.',
            });
        } catch (error) {
            console.error(error);
            showError({
                title: 'Save Failed',
                text: 'Unable to update reminder settings. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (!show) {
        return null;
    }

    return (
        <div className="user-reminder-modal" role="dialog" aria-modal="true" aria-labelledby="user-reminder-title">
            <button
                type="button"
                className="user-reminder-modal__backdrop"
                aria-label="Close reminder settings"
                onClick={onClose}
            />
            <div className="user-reminder-modal__dialog">
                <div className="user-reminder-modal__header">
                    <div>
                        <h5 id="user-reminder-title" className="user-reminder-modal__title">
                            <i className="bi bi-bell me-2"></i>
                            My Reminder Settings
                        </h5>
                        <p className="user-reminder-modal__subtitle mb-0">
                            Personal reminder preferences for {userName}
                        </p>
                    </div>
                    <div className="user-reminder-modal__header-actions">
                        {!isLoading && !loadFailed && settings && !isEditing && (
                            <button
                                type="button"
                                className="btn btn-sm btn-light-primary rounded-pill"
                                onClick={handleEdit}
                            >
                                <i className="bi bi-pencil me-1"></i>
                                {settingsMissing ? 'Create' : 'Edit'}
                            </button>
                        )}
                        {isEditing && (
                            <ClinicSettingsCancelButton onClick={handleCancel} disabled={isSaving} />
                        )}
                        <button
                            type="button"
                            className="user-reminder-modal__close"
                            onClick={onClose}
                            aria-label="Close"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                </div>

                <div className="user-reminder-modal__body">
                    {isLoading ? (
                        <>
                            <ReminderOverviewStats allItemsCount={0} enabledCount={0} activeChannels={0} />
                            <ClinicSettingsListSkeleton rows={6} />
                        </>
                    ) : loadFailed ? (
                        <div className="alert alert-danger d-flex align-items-center justify-content-between gap-3 mb-0">
                            <span>
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                Failed to load reminder settings.
                            </span>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger rounded-pill"
                                onClick={fetchSettings}
                            >
                                Retry
                            </button>
                        </div>
                    ) : (
                        <>
                            {settingsMissing && !isEditing && (
                                <div className="alert alert-warning d-flex align-items-center justify-content-between gap-3">
                                    <span>
                                        <i className="bi bi-info-circle me-2"></i>
                                        No personal reminder settings found. Create settings to get started.
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-warning rounded-pill"
                                        onClick={handleEdit}
                                    >
                                        <i className="bi bi-plus-circle me-1"></i>
                                        Create Settings
                                    </button>
                                </div>
                            )}

                            <ReminderOverviewStats
                                allItemsCount={allItems.length}
                                enabledCount={enabledCount}
                                activeChannels={activeChannels}
                            />

                            <form onSubmit={handleSubmit(onSubmit)} className="reminder-settings-form">
                                <ReminderSettingsPanels
                                    values={currentValues}
                                    isEditing={isEditing}
                                    register={register}
                                    control={control}
                                    errors={errors}
                                    fieldIdPrefix={fieldIdPrefix}
                                />

                                {isEditing && (
                                    <div className="reminder-settings-form__footer">
                                        <ClinicSettingsFormFooter
                                            isSaving={isSaving}
                                            submitLabel={settingsMissing ? 'Create Settings' : 'Save Changes'}
                                        />
                                    </div>
                                )}
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserReminder;
